import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ResizableBox } from 'react-resizable';
import PropTypes from 'prop-types';
import { Col, Row, Divider, Button, Tooltip, message, Popover } from 'antd';
import _ from 'lodash';

import ComponentWrapper from '../ComponentWrapper/ComponentWrapper';
import Uploader from 'components/Uploader';
import Loader from 'components/Loader';
import ShadowPicker from 'components/ShadowPicker/ShadowPicker';
import BorderPicker from 'components/BorderPicker/BorderPicker';
import { ImageEditor } from './components';
import {
  DeleteIcon,
  LeftAlignIcon,
  CenterAlignIcon,
  RightAlignIcon,
  HyperLinkIcon,
  CopyIcon,
  PasteIcon,
  DropdownIcon,
} from 'components/Icons';
import AIIcon from 'components/Icons/AIIcon';
import ReplaceUploadIcon from 'components/Icons/ReplaceUploadIcon';
import { useProposalContext } from 'context/proposal';
import useWindowDimensions from 'hooks/useWindowDimensions';
import useSavingTimeout from 'hooks/useSavingTimeout';

import getCroppedImg from './helpers/cropImage';
import helpers from 'helpers';
import { resizeImage } from 'utils/image';
import { getShadow, getBorder } from 'utils/colors';

import { DEFAULT_SHADOW_BORDER_COLOR } from 'constants/colors';

import './ImageComponent.scss';
import 'react-resizable/css/styles.css';

const INFINITY = 30000;
const CROP_OPTIONS = [
  { id: 0, width: 1, height: 1, label: '1:1' },
  { id: 1, width: 3, height: 4, label: '3:4' },
  { id: 2, width: 4, height: 3, label: '4:3' },
  { id: 3, width: 0, height: 0, label: 'Full' },
];

const DEFAULT_CONFIG = {
  opacity: 1,
  editable: true,
  size: { width: 900, height: 2000, ratio: 1 },
  alignment: 'left',
  border: {},
  shadow: {},
};

const ImageComponent = ({ block, blockProps }) => {
  const { width: windowWidth } = useWindowDimensions();
  const isRtl = ['hebrew', 'arabic'].includes(blockProps.language);
  const blockKey = block.getKey();

  const ent = block.getEntityAt(0);
  const entity =
    ent && blockProps?.editorState
      ? blockProps?.editorState?.getCurrentContent().getEntity(ent)
      : null;

  const isSection = blockProps.type === 'section-image';
  const resizefactor = blockProps.resizefactor || 1;

  const { tempState, setTempState, setSavingDraftProposal } = useProposalContext();
  const startSavingTimeout = useSavingTimeout(() => {
    setSavingDraftProposal(false);
  }, 10000);

  const [resizeWidth, setResizeWidth] = useState((blockProps.resizeWidth || 530) / resizefactor);
  const [loadedDimension, setLoadedDimension] = useState(null);
  const imageInputRef = useRef();
  const imageContainer = useRef();
  const { data } = entity;
  const imgContent = helpers.formatS3Link(data?.src);
  const [optionList, setOptionList] = useState([...CROP_OPTIONS]);
  const [config, setConfig] = useState({
    ...DEFAULT_CONFIG,
    size: { width: resizeWidth, height: INFINITY, ratio: 1 },
    alignment: isRtl ? 'right' : 'left',
    border: data?.config?.border || {},
    shadow: data?.config?.shadow || {},
    opacity: data?.config?.opacity ?? 1,
    imageType: 'unknown',
  });
  const [hyperlink, setHyperlink] = useState(data?.hyperlink || '');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [activeWidth, setActiveWidth] = useState({
    id: 3,
    width: 0,
    height: 0,
  });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(imgContent);
  const [shadow, setShadow] = useState(config?.shadow || {});
  const [border, setBorder] = useState(config?.border || {});
  const [isSaving, setIsSaving] = useState(false);
  const [hideImage, setHideImage] = useState(false);
  const [localToolOpen, setLocalToolOpen] = useState(false);
  const [lastEdited, setLastEdited] = useState(null); // prevent jumping by ignoring the outdated incoming data
  const [showReplaceOptions, setShowReplaceOptions] = useState(false);
  const [resizing, setResizing] = useState(false);

  const [upload] = Uploader();
  const { toolOpen } = blockProps;

  const setToolOpen = (toolOpenValue) => {
    blockProps.dispatch({
      type: 'setToolOpen',
      toolOpen: toolOpenValue,
    });
    setLocalToolOpen(toolOpenValue);
  };

  // Sync local toolOpen state with blockProps toolOpen
  useEffect(() => {
    setLocalToolOpen(toolOpen);
  }, [toolOpen]);

  useEffect(() => {
    setConfig((prev) => {
      // on crop, change ratio and height of image
      const ratio = getRatio(activeWidth.width, activeWidth.height);
      const newConfig = {
        ...prev,
        size: {
          ...prev.size,
          ratio,
          height: prev.size.width / ratio,
        },
      };

      return newConfig;
    });
  }, [activeWidth]);

  useEffect(() => {
    if (data?.config) {
      // if edited is older than lastEdited then ignore the update (this prevents jumping while resizing)
      if (!lastEdited || new Date(blockProps.proposal.edited).getTime() > lastEdited) {
        setConfig((prev) => ({ ...prev, ...JSON.parse(JSON.stringify(data.config)) }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Force re-render when entity data changes (especially image src)
  useEffect(() => {
    if (data?.src) {
      setCroppedImage(helpers.formatS3Link(data.src));
    }
  }, [data?.src]);

  useEffect(() => {
    if (isSection) {
      setResizeWidth(900);
    } else {
      // NOTE: on blockProps.titleFontPosition change useEffect is not happening
      const { titleFontPosition } = blockProps;
      // on changing title font position change max width
      const _width = ['top', 'top-middle', 'none'].includes(titleFontPosition)
        ? 796
        : titleFontPosition === 'large'
        ? 664
        : 530;
      setResizeWidth(_width / resizefactor);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockProps.titleFontPosition, resizefactor]);

  useEffect(() => {
    // on section layout chnage
    if (resizeWidth && !isSection) {
      setConfig((prev) => {
        const newConfig = { ...prev };

        // when column width changes and width of old image
        // is bigger than new column width change old width & height
        if (!newConfig?.size?.width || newConfig.size.width > resizeWidth) {
          if (!newConfig?.size) {
            newConfig.size = {};
          }
          newConfig.size.width = resizeWidth;

          if (newConfig.size?.ratio) {
            newConfig.size.height = newConfig.size.width / newConfig.size.ratio;
          } else {
            let ratio = 1;
            if (prev?.size?.width) {
              ratio = getRatio(prev.size.width, prev.size.height);
            } else if (loadedDimension?.width) {
              ratio = getRatio(loadedDimension.width, loadedDimension.height);
            }
            newConfig.size.height = newConfig.size.width / ratio;
          }
        }

        return newConfig;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizeWidth]);

  const handleOpacity = (opacity) => {
    setConfig((prev) => ({ ...prev, opacity }));
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const saveImage = useCallback(
    async (file) => {
      const entityKey = block.getEntityAt(0);
      if (entityKey) {
        const ratio = getRatio(activeWidth.width, activeWidth.height);
        const { url } = await upload(file, `props/${blockProps.proposalId}/section`);
        const newData = JSON.parse(JSON.stringify(data));
        newData.config = { ...config };
        newData.src = url;
        if (!newData?.config?.size) {
          newData.config.size = {};
        }
        newData.config.size.ratio = ratio;
        delete newData.config.size?.height;

        setConfig({ ...newData.config });

        const currentContentState = blockProps.editorState?.getCurrentContent();
        if (currentContentState) {
          currentContentState.replaceEntityData(entityKey, { ...newData });
        }
        blockProps.update();
        setCroppedImage(url);
        setToolOpen(false);
        setIsSaving(false);
      } else {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [block, blockProps, data, upload, config, activeWidth]
  );

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(imgContent, croppedAreaPixels, rotation);
      await saveImage(croppedImage);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, rotation, imgContent, saveImage]);

  const onSave = () => {
    // image edit options didn't change so dont save new image
    if (
      crop.x === 0 &&
      crop.y === 0 &&
      rotation === 0 &&
      zoom === 1 &&
      config?.opacity === 1 &&
      activeWidth.id === 3
    ) {
      return cancelEditing();
    }
    setSavingDraftProposal(true);
    setIsSaving(true);
    showCroppedImage();
  };

  const handleRemove = () => blockProps?.onRemove(block.getKey());

  const handleEdit = (value = true) => {
    value?.preventDefault();
    // update the config with the new image
    const { width, height } = config?.size;
    if (width && height) {
      // update the option list for 'full' crop
      setOptionList((prev) => {
        const newData = [...prev];
        newData[3] = { ...optionList[3], width, height };

        if (activeWidth.id === 3) {
          setActiveWidth(newData[3]);
        }
        return newData;
      });
    }

    if (!value) {
      setConfig((prev) => ({
        ...prev,
        opacity: data?.config?.opacity ?? 1,
      }));
    }

    setToolOpen('edit');
    setTimeout(() => {
      imageContainer?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const handleBlur = () => {
    blockProps.handleEditComponent(false);
    setToolOpen(false);
  };

  const cancelEditing = () => {
    setZoom(1);
    setRotation(0);
    setConfig((prev) => ({
      ...prev,
      opacity: data?.config?.opacity ?? 1,
    }));
    setActiveWidth(optionList[3]);
    setToolOpen(false);
  };

  const saveConfigs = useCallback(
    (newConfig) => {
      const entityKey = block.getEntityAt(0);
      if (entityKey && Object.keys(newConfig).length > 0) {
        const newData = data;
        newData.config = JSON.parse(JSON.stringify({ ...newData.config, ...newConfig }));
        const currentContentState = blockProps.editorState?.getCurrentContent();
        if (currentContentState) {
          currentContentState.replaceEntityData(entityKey, { ...newData });
        }
        blockProps.update(true);
      }
    },
    [block, data, blockProps]
  );

  const debouncedSave = useCallback(
    (key, value) => {
      const debouncedFn = _.debounce(() => {
        saveConfigs({ [key]: value });
        startSavingTimeout();
      }, 200);
      debouncedFn();
    },
    [saveConfigs, startSavingTimeout]
  );

  const handlePropertyChange = (key, value) => {
    if (key === 'shadow') {
      setShadow(value);
    } else if (key === 'border') {
      setBorder(value);
    }
    setSavingDraftProposal(true);
    debouncedSave(key, value);
  };

  const onReplace = () => {
    if (blockProps.showImageSelectionModal) {
      if (blockProps.setCurrentImageBlock) {
        blockProps.setCurrentImageBlock(blockKey);
      }
      blockProps.showImageSelectionModal(true, 'upload');
    }
  };

  const onImageSelect = async (event) => {
    event.preventDefault();
    const { files } = event.target;
    const [file] = files;
    if (!file) return;

    // Only allow image files
    if (file && !file.type.startsWith('image/')) {
      message.error('Only image is allowed.');
      return;
    }

    const entityKey = block.getEntityAt(0);
    if (entityKey) {
      const resizedImage = await resizeImage({
        file,
        path: 'image',
        maxWidth: 950,
      });

      const { url } = await blockProps.upload(
        resizedImage,
        `props/${blockProps.proposalId}/section`
      );

      const img = new Image();
      img.src = url;

      img.onload = async () => {
        const { width, height } = img;
        setConfig((prev) => {
          const newData = {
            ...data,
            src: url,
            config: {
              ...prev,
              imageType: files[0].type.substring(6),
              size: {
                ratio: getRatio(width, height),
              },
            },
          };

          const currentContentState = blockProps.editorState?.getCurrentContent();
          if (currentContentState) {
            currentContentState.replaceEntityData(entityKey, { ...newData });
          }
          setHideImage(true);
          blockProps.update();

          return { ...newData.config };
        });

        setCroppedImage(url);
        setHideImage(false);
      };
    } else {
      setIsSaving(false);
    }
  };

  const boxShadow = getShadow(shadow);
  const borderStyle = getBorder(border);

  const constructSize = (size, width, height, maxHeight, ratio) => {
    return {
      ...size,
      width,
      height,
      ...(maxHeight ? { maxHeight } : {}),
      ratio: ratio || getRatio(width, height),
    };
  };

  const saveResize = (value, _config) => {
    const { width, height } = value;
    if (!width || !height) {
      return;
    }
    const entityKey = block.getEntityAt(0);
    if (entityKey && value) {
      const newData = data || {};
      if (_config) {
        newData.config = _config;
      } else {
        if (newData.config === undefined) {
          newData.config = {};
        }
        newData.config.size = constructSize(config.size, width, height);
      }
      const currentContentState = blockProps.editorState?.getCurrentContent();
      if (currentContentState) {
        currentContentState.replaceEntityData(entityKey, { ...newData });
      }
      blockProps.update(true);
    }
  };

  const onResize = (e, resizeData) => {
    e.preventDefault();
    let { width, height } = resizeData.size;

    setConfig((prev) => {
      return {
        ...prev,
        size: constructSize(prev.size, width || prev.size?.width, height || prev.size?.height),
      };
    });

    setLastEdited(Date.now() + 100);
  };

  const onResizeStart = (e) => {
    e.preventDefault();
    setToolOpen(false);
    setResizing(true);
  };

  const onResizeStop = (e, resizeData) => {
    e.preventDefault();
    saveResize(resizeData.size, config);
    setResizing(false);
  };

  const handleImageAlignment = (alignment) => {
    const entityKey = block.getEntityAt(0);
    if (entityKey && alignment) {
      setConfig((prev) => {
        const newConfig = { ...prev, alignment: alignment };
        const newData = data;
        newData.config = newConfig;
        const currentContentState = blockProps.editorState?.getCurrentContent();
        if (currentContentState) {
          currentContentState.replaceEntityData(entityKey, { ...newData });
        }
        return newConfig;
      });
      blockProps.update(true);
      setLastEdited(Date.now() + 100);
    }
  };

  const getRatio = (width, height) => {
    return parseFloat((width / height).toFixed(2));
  };

  const handleOnLoad = (e) => {
    const { offsetHeight, offsetWidth } = e.target;
    let height = offsetHeight;
    let width = Math.min(offsetWidth, resizeWidth, 900);
    let { ratio } = data?.config?.size || {};
    if (ratio) {
      height = width / ratio;
    }

    setLoadedDimension({ height, width });

    if (!data?.config?.size?.width || !data?.config?.size?.height) {
      // w x h , 100 x 50 = ratio 2
      // w = 40
      // h = ? W/ratio
      const _config = {
        ...config,
        editable: true,
        size: constructSize({}, width, height, height, ratio),
        alignment: data?.config?.alignment || (isRtl ? 'right' : 'left'),
      };

      setConfig(_config);
      saveResize(_config.size, _config);
    }

    getImageType();
  };

  const getImageType = () => {
    if (config?.imageType && config.imageType !== 'unknown') {
      return;
    }
    if (croppedImage.endsWith('.gif')) {
      setConfig((prev) => ({ ...prev, imageType: 'gif' }));
    }
  };

  const handleDeleteVisibleChange = () => {
    setToolOpen(false);
    handleBlur();
  };

  const onHyperLinkClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { dispatch, uniqueKey } = blockProps;
    let shouldOpen = true;

    const element = document.getElementById(`img-hyperlink-${uniqueKey}`);
    if (element) {
      if (element.style.display === 'none') {
        // if display is none, then close it and open fresh
        dispatch({
          type: 'setDisplayHyperlinkInput',
          displayHyperlinkInput: false,
        });
      } else {
        // if display is block, then close it
        shouldOpen = false;
      }
    }

    // hide all hyperlink modal
    const elements = document.querySelectorAll('.hyperlink-content');
    elements.forEach((el) => {
      el.style.display = 'none';
    });

    const actionButtonBound = event.target
      .closest('.rich-editor-components-action-buttons')
      .getBoundingClientRect();
    const sectionBound = event.target.closest('.simple-section-content').getBoundingClientRect();

    let y = actionButtonBound.top - sectionBound.top + 25;
    if (actionButtonBound.bottom + 80 > window.innerHeight) {
      y -= 120;
    }

    if (shouldOpen) {
      setTimeout(() => {
        dispatch({
          type: 'setDisplayHyperlinkInput',
          displayHyperlinkInput: {
            position: {
              display: 'block',
              top: `${y + Math.random()}px`,
            },
            link: hyperlink,
            onSubmit: handleHyperLinkSave,
          },
        });
      }, 20);
    } else {
      dispatch({
        type: 'setDisplayHyperlinkInput',
        displayHyperlinkInput: false,
      });
    }
  };

  const handleHyperLinkSave = (value) => {
    const entityKey = block.getEntityAt(0);
    if (entityKey) {
      const newData = data;
      newData.hyperlink = value;
      const currentContentState = blockProps.editorState?.getCurrentContent();
      if (currentContentState) {
        currentContentState.replaceEntityData(entityKey, { ...newData });
      }
      setHyperlink(value);
      blockProps.update(true);
    }
  };

  const copyStyle = () => {
    const color = { ...DEFAULT_SHADOW_BORDER_COLOR };
    const copiedStyle = {
      shadow: { ...color },
      border: { ...color },
      alignment: config.alignment,
    };
    const shadowData = { ...config.shadow, ...shadow };
    const borderData = { ...config.border, ...border };

    if (shadowData?.hueColor) {
      copiedStyle.shadow = {
        ...shadowData,
        hueColor: {
          rgb: shadowData.hueColor?.rgb || color.rgb,
          hex: shadowData.hueColor?.hex || color.hex,
        },
      };
    }

    if (borderData?.borderColor) {
      copiedStyle.border = {
        ...borderData,
        borderColor: {
          hex: borderData.borderColor?.hex || color.hex,
        },
      };
    }

    // store the copied style in temporary state
    setTempState({ type: 'image-style', data: copiedStyle });

    message.success('Copied image styles');
  };

  const pasteStyle = () => {
    if (tempState?.type === 'image-style') {
      const { shadow = {}, border = {}, alignment = 'left' } = tempState.data;

      setShadow(shadow);
      setBorder(border);
      handleImageAlignment(alignment);

      setTimeout(() => {
        saveConfigs({
          shadow: shadow,
          border: border,
          alignment: alignment,
        });
      }, 50);
    }
  };

  const makeEditorReadOnly = (visible) => {
    if (visible) {
      setTimeout(() => {
        blockProps.dispatch({
          type: 'setEditorReadOnly',
          editorReadOnly: true,
        });
      }, 20);
    } else {
      blockProps.dispatch({
        type: 'setEditorReadOnly',
        editorReadOnly: false,
      });
    }
  };

  // find width
  let width = isSection
    ? 900
    : (config?.size?.width && window?.screen?.width > 480) ||
      (window?.screen?.width < 480 && config?.size?.width < 360)
    ? config?.size?.width || resizeWidth
    : INFINITY;
  width = Math.min(window.innerWidth - 62, resizeWidth, width);

  // find ratio
  let ratio = config?.size?.ratio || getRatio(width || resizeWidth, config?.size?.height || 400);

  // find height
  let height = isSection && config?.size?.height ? config.size.height : width / ratio;

  const maxWidth = resizeWidth;
  const maxHeight = isSection ? INFINITY : maxWidth / ratio;
  const popoverVisible = localToolOpen === 'shadow' || localToolOpen === 'border';

  if (!config?.size?.height) {
    /* if height not found render the dummy image, 
    handleOnLoad will get the height and set the image ratio */
    return (
      <img
        src={croppedImage}
        onLoad={handleOnLoad}
        alt="dummy"
        style={{
          width: isSection ? '100%' : `calc(100% - 2px)`,
        }}
      />
    );
  }

  const alignmentTool = !isSection && (
    <>
      <Tooltip title="Left Align">
        <LeftAlignIcon
          className={`alignment-icon ${config?.alignment === 'left' ? 'anticon-active' : ''}`}
          onClick={() => handleImageAlignment('left')}
        />
      </Tooltip>
      <Tooltip title="Center Align">
        <CenterAlignIcon
          className={`alignment-icon ${config?.alignment === 'center' ? 'anticon-active' : ''}`}
          onClick={() => handleImageAlignment('center')}
        />
      </Tooltip>
      <Tooltip title="Right Align">
        <RightAlignIcon
          className={`alignment-icon ${config?.alignment === 'right' ? 'anticon-active' : ''}`}
          onClick={() => handleImageAlignment('right')}
        />
      </Tooltip>
    </>
  );

  const onRegenerate = () => {
    if (blockProps.showImageSelectionModal) {
      if (blockProps.setCurrentImageBlock) {
        blockProps.setCurrentImageBlock(blockKey);
      }
      blockProps.showImageSelectionModal(true, 'regenerate');
    }
  };

  return (
    <>
      <ComponentWrapper
        isSection={isSection}
        sectionName={blockProps.sectionName}
        setDraggingElement={blockProps.setDraggingElement}
        showDrag={popoverVisible || isSection ? false : true}
        showActionButtons={true}
        showImageActions={true}
        config={config}
        hyperlink={hyperlink}
        border={border}
        showDelete={false}
        isDraggable={!popoverVisible}
        blockKey={blockKey}
        remove={handleRemove}
        onEdit={null}
        onBlur={popoverVisible ? null : handleBlur}
        componentType="Image"
        handleEditComponent={blockProps.handleEditComponent}
        isSaving={isSaving}
        onHyperLinkSave={handleHyperLinkSave}
        setDropDisabled={blockProps.setDropDisabled}
        componentWrapperStyle={
          localToolOpen === 'edit' ? {} : { width: width, height: height + 35 }
        }
        toolbarClassName="image-toolbar"
        toolbar={
          !resizing &&
          localToolOpen !== 'edit' && (
            <>
              <Col>
                {config?.imageType !== 'gif' && <span onClick={handleEdit}>Edit</span>}
                <span className="image-options dropdown">
                  <span onClick={() => setShowReplaceOptions(true)}>
                    Change Image
                    <DropdownIcon fill="#005aff" />
                  </span>
                </span>
                {showReplaceOptions && (
                  <div
                    className="image-options-dropdown"
                    onMouseLeave={() => setShowReplaceOptions(false)}>
                    <div className="image-options-group">
                      <div className="image-options-item" onClick={onRegenerate}>
                        <AIIcon className="image-options-icon" />
                        <span>Regenerate</span>
                      </div>
                      <div
                        className="image-options-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={onReplace}>
                        <ReplaceUploadIcon className="image-options-icon" />
                        <span>Replace</span>
                      </div>
                    </div>
                  </div>
                )}

                <span className={isSaving ? 'rich-editor-components-action-disabled' : ''}>
                  <Tooltip title="Hyperlink" onClick={onHyperLinkClick}>
                    <HyperLinkIcon />
                  </Tooltip>
                </span>
                <span className="component-tool">
                  {!isSection && (
                    <span
                      className="tool-button"
                      onClick={() => {
                        setToolOpen(localToolOpen === 'delete' ? false : 'delete');
                        blockProps.displayAIToolBarCursor.current = '';
                      }}>
                      <span className={isSaving ? 'rich-editor-components-action-disabled' : ''}>
                        <Tooltip title="Delete">
                          <DeleteIcon className="delete-icon" />
                        </Tooltip>
                      </span>
                    </span>
                  )}
                  {localToolOpen === 'delete' && (
                    <div className="tool-wrapper delete-picker-wrapper Prosprich-editor-components-popover">
                      <div className="ant-popover-inner">
                        <div className="ant-popover-inner-content">
                          <Col className="table-delete-popover">
                            <h3 className="ant-popover-title">Delete image?</h3>
                            <Divider />
                            <Row className="button-wrapper">
                              <Button className="medium-btn secondary-btn" onClick={handleRemove}>
                                Delete
                              </Button>
                              <Button
                                className="medium-btn grey-btn"
                                onClick={() => handleDeleteVisibleChange(false)}>
                                Cancel
                              </Button>
                            </Row>
                          </Col>
                        </div>
                      </div>
                    </div>
                  )}
                </span>
              </Col>

              {windowWidth < 992 && <Col className="below-lg">{alignmentTool}</Col>}

              <Col>
                <span className="component-tool">
                  <Popover
                    placement="top"
                    content={
                      <ShadowPicker
                        shadow={shadow}
                        onChange={(value) => handlePropertyChange('shadow', value)}
                      />
                    }
                    onVisibleChange={(visible) => {
                      setToolOpen(visible ? 'shadow' : false);
                      makeEditorReadOnly(visible);
                    }}
                    trigger="click"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    overlayClassName="tool-wrapper shadow-picker-wrapper Prosprich-editor-components-popover">
                    <span className="tool-button">Shadow</span>
                  </Popover>
                </span>

                <span className="component-tool">
                  <Popover
                    placement="top"
                    content={
                      <BorderPicker
                        border={border}
                        onChange={(value) => {
                          handlePropertyChange('border', value);
                        }}
                      />
                    }
                    onVisibleChange={(visible) => {
                      setToolOpen(visible ? 'border' : false);
                      makeEditorReadOnly(visible);
                    }}
                    trigger="click"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    overlayClassName="tool-wrapper border-picker-wrapper Prosprich-editor-components-popover">
                    <span className="tool-button">Border</span>
                  </Popover>
                </span>

                {windowWidth >= 992 && alignmentTool}

                <span className="tool-label style-label">Style</span>

                <Tooltip title="Copy styles">
                  <span className="component-tool copy-icon">
                    <CopyIcon onClick={copyStyle} />
                  </span>
                </Tooltip>

                <Tooltip title="Paste styles">
                  <span
                    className={`component-tool paste-icon ${
                      tempState?.type === 'image-style' ? '' : 'disabled'
                    }`}>
                    <PasteIcon onClick={pasteStyle} />
                  </span>
                </Tooltip>
              </Col>
            </>
          )
        }>
        {localToolOpen === 'edit' ? (
          <div className="image-component-container img-249" ref={imageContainer}>
            {isSaving && <Loader overlay />}
            <ImageEditor
              optionList={optionList}
              imgContent={imgContent}
              crop={crop}
              rotation={rotation}
              zoom={zoom}
              opacity={config.opacity}
              activeWidth={activeWidth}
              setCrop={setCrop}
              setRotation={setRotation}
              onCropComplete={onCropComplete}
              setZoom={setZoom}
              setOpacity={handleOpacity}
              setActiveWidth={setActiveWidth}
            />

            <div className="edit-image-action-btns">
              <Button
                size="middle"
                type="button"
                className={`small-btn secondary-btn ${
                  isSaving ? 'rich-editor-components-action-disabled' : ''
                }`}
                onClick={onSave}>
                SAVE
              </Button>
              <Button
                size="middle"
                type="button"
                className={`small-btn grey-btn ${
                  isSaving ? 'rich-editor-components-action-disabled' : ''
                }`}
                onClick={cancelEditing}>
                CANCEL
              </Button>
            </div>
          </div>
        ) : !imgContent ? (
          <div className="gallery-container" onClick={blockProps.editComponent}>
            <div className="gallery-image-container">
              <div className="gallery-uploader">
                <div>Upload Image</div>
              </div>
            </div>
          </div>
        ) : (
          <ResizableBox
            draggableOpts={{ offsetParent: document.body, grid: [2, 2] }}
            lockAspectRatio={isSection ? false : true}
            axis={'both'}
            width={width}
            height={height}
            onResize={onResize}
            onResizeStart={onResizeStart}
            onResizeStop={onResizeStop}
            resizeHandles={isSection ? ['s'] : isRtl ? ['sw'] : ['se']}
            minConstraints={ratio > 1 ? [ratio * 50, 50] : [50, (1 / ratio) * 50]}
            maxConstraints={[maxWidth, isSection ? INFINITY : maxHeight]}
            className={`image-component-container img-311`}>
            {!hideImage && (
              <img
                src={croppedImage}
                onLoad={handleOnLoad}
                alt="cropped"
                style={{
                  width: isSection ? '100%' : `calc(100% - 2px)`,
                  height: '100%',
                  // aspectRatio: `${ratio}`,
                  opacity: config?.opacity ?? 1,
                  boxShadow: boxShadow,
                  border: borderStyle,
                  borderRadius: border.borderRadius || 0,
                }}
              />
            )}
          </ResizableBox>
        )}
      </ComponentWrapper>
      <input
        type="file"
        ref={imageInputRef}
        style={{ display: 'none' }}
        onChange={onImageSelect}
        accept="image/*"
      />
    </>
  );
};

ImageComponent.propTypes = {
  block: PropTypes.instanceOf(Object).isRequired,
  blockProps: PropTypes.instanceOf(Object).isRequired,
};

export default ImageComponent;

/* put on image wrap when enabling width editing: style={{width: data.width || '30%' }} */
